import { EmptyState, Skeleton } from "@design";
import { ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Page } from "@/components/Page";
import { Text } from "@/components/Text";
import { ApprovalItem } from "./ApprovalItem";
import { useApprovals, useDecideApproval } from "./useApprovals";

/**
 * The approvals inbox (MHS-972, docs/design/extensions-integrations/WRITE_ACTIONS_DESIGN.md): write actions my
 * workflows want to take while nobody is watching (send an email, post to Slack...). Each run waits here until I
 * approve or decline its action, or for a day, after which it goes on without it.
 */
export function ApprovalsPage() {
  const { t } = useTranslation("workflows", { keyPrefix: "approvals" });
  const approvals = useApprovals();
  const decide = useDecideApproval();

  let body: ReactNode;
  if (approvals.isPending) {
    body = <Skeleton className="h-48 w-full" />;
  } else if (approvals.isError) {
    body = (
      <Text role="alert" tone="error">
        {t("loadFailed")}
      </Text>
    );
  } else if (approvals.data.length === 0) {
    body = <EmptyState icon={<ShieldCheck />} title={t("emptyTitle")} body={t("emptyBody")} />;
  } else {
    body = (
      <section aria-label={t("listLabel")} className="flex flex-col gap-3">
        {approvals.data.map((request) => (
          <ApprovalItem
            key={request.uuid}
            request={request}
            onDecide={({ decision, overrides, remember }, onFailed) =>
              decide.mutate(
                { path: { request_uuid: request.uuid }, body: { decision, overrides, remember } },
                { onError: onFailed },
              )
            }
          />
        ))}
      </section>
    );
  }

  return (
    <Page title={t("title")} subtitle={t("subtitle")}>
      {body}
    </Page>
  );
}
